import { useEffect, useMemo, useRef, useState } from 'react';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import FormattedDateInput from '../../components/ui/FormattedDateInput';
import WizardSelect from '../../components/ui/WizardSelect';
import { IconWarningTriangle } from '../../components/icons/WizardIcons';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../../components/criminal/CriminalReviewUi';
import { useCriminalLookups } from '../../hooks/useCriminalLookups';
import type { LookupItem } from '../../mocks/criminalMasterData';
import {
  getCriminalRequestViewData,
  saveCriminalRequest,
  type CriminalRequestViewData,
} from '../../services/criminalCaseRequests';
import type { CriminalWizardStep } from '../../services/criminalCaseMapper';
import type { CriminalSaveStep1, CriminalSaveStep3, CriminalSaveStep4 } from '../../types/criminalApi';
import type { AccusedRecord, CriminalWizardDraft } from '../../types/criminalCaseRequest';
import {
  calcAgeFromDob,
  exceedsMax,
  isFutureIsoDate,
  maxCharLimitExceededError,
  sliceMax,
  todayIsoDateLocal,
  tomorrowIsoDateLocal,
} from '../../utils/caseFieldValidation';
import { isBriberySubCategory, isOtherLookup } from '../../utils/criminalConstants';
import { setFlashToast } from '../../utils/flashToast';
import {
  focusFirstInvalid,
  validateRequired,
  type RequiredErrors,
  type RequiredFieldSpec,
} from '../../utils/requiredValidation';
import { FieldError, inpClass } from './criminalFieldUi';
import {
  canSaveCaseUpdateStep,
  confirmCaseUpdateCancel,
  formatSubmittedAtLabel,
  isCaseRequestUuid,
  mergeCriminalSavedStepIntoInitial,
  submitterFromStep4,
  toSelectOpts,
} from './taskCaseUpdateHelpers';

type Props = {
  caseRequestId: string;
  recentSubmittedAt: string;
  readOnly?: boolean;
  /** Preloaded criminal steps payload from task details page (avoids duplicate GET). */
  viewData?: CriminalRequestViewData | null;
  viewDataLoading?: boolean;
  onCancelConfirmed?: () => void;
};

const MAX_DESC = 2000;
const MAX_REF = 255;
const MAX_TEXT = 2000;

function cloneDraft(d: CriminalWizardDraft): CriminalWizardDraft {
  return {
    ...d,
    applicableAreas: [...d.applicableAreas],
    applicableAreaIds: [...d.applicableAreaIds],
    accused: d.accused.map((a) => ({ ...a })),
    deliveryMethodIds: [...d.deliveryMethodIds],
    deliveryMethods: [...d.deliveryMethods],
  };
}

function draftHasUnsavedChanges(current: CriminalWizardDraft, saved: CriminalWizardDraft): boolean {
  return JSON.stringify(current) !== JSON.stringify(saved);
}

function newAccused(): AccusedRecord {
  return { id: crypto.randomUUID(), name: '', status: '', statusOther: '' };
}

function isStatusOther(statusId: string, statuses: LookupItem[]): boolean {
  const row = statuses.find((s) => s.id === statusId);
  return row ? isOtherLookup(row.name, row.id) : statusId === 'other';
}

function TrashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

function applyViewData(
  data: CriminalRequestViewData,
  recentSubmittedAt: string,
): {
  draft: CriminalWizardDraft;
  step1: CriminalSaveStep1 | undefined;
  step3: CriminalSaveStep3 | undefined;
  step4: CriminalSaveStep4 | undefined;
  submittedLabel: string;
} {
  const loaded = cloneDraft(data.draft);
  return {
    draft: loaded,
    step1: data.step1,
    step3: data.step3,
    step4: data.step4,
    submittedLabel: formatSubmittedAtLabel(data.submittedAt, recentSubmittedAt),
  };
}

export default function TaskCaseUpdateTab({
  caseRequestId,
  recentSubmittedAt,
  readOnly = false,
  viewData,
  viewDataLoading = false,
  onCancelConfirmed,
}: Props) {
  const [draft, setDraft] = useState<CriminalWizardDraft | null>(null);
  const [initial, setInitial] = useState<CriminalWizardDraft | null>(null);
  const [step1, setStep1] = useState<CriminalSaveStep1 | undefined>();
  const [step3, setStep3] = useState<CriminalSaveStep3 | undefined>();
  const [step4, setStep4] = useState<CriminalSaveStep4 | undefined>();
  const [submittedLabel, setSubmittedLabel] = useState(recentSubmittedAt);
  const [loadedForKey, setLoadedForKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState('');
  const [savingStep, setSavingStep] = useState<CriminalWizardStep | null>(null);
  const saveInFlightRef = useRef(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [errors, setErrors] = useState<RequiredErrors>({});
  const isSaving = savingStep !== null;

  const lookups = useCriminalLookups('review', draft?.provinceId ?? '', draft?.districtId ?? '');

  const useSharedViewData = viewData !== undefined || viewDataLoading;

  const loadKey = `${caseRequestId}|${recentSubmittedAt}|${viewData?.submittedAt ?? ''}|${viewData?.draft.draftRequestId ?? ''}`;
  const loading = useSharedViewData
    ? viewDataLoading || loadedForKey !== loadKey
    : loadedForKey !== loadKey;

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      if (!isCaseRequestUuid(caseRequestId)) {
        if (cancelled) return;
        setDraft(null);
        setInitial(null);
        setLoadError('Case request id is missing or invalid.');
        setLoadedForKey(loadKey);
        return;
      }

      if (useSharedViewData) {
        if (viewDataLoading) return;
        if (!viewData?.draft) {
          if (cancelled) return;
          setDraft(null);
          setInitial(null);
          setStep1(undefined);
          setStep3(undefined);
          setStep4(undefined);
          setLoadError('Could not load case update data.');
          setLoadedForKey(loadKey);
          return;
        }

        const applied = applyViewData(viewData, recentSubmittedAt);
        if (cancelled) return;
        setDraft(applied.draft);
        setInitial(cloneDraft(applied.draft));
        setStep1(applied.step1);
        setStep3(applied.step3);
        setStep4(applied.step4);
        setSubmittedLabel(applied.submittedLabel);
        setLoadError('');
        setLoadedForKey(loadKey);
        return;
      }

      const res = await getCriminalRequestViewData(caseRequestId);
      if (cancelled) return;

      if (!res.success || !res.data?.draft) {
        const errMsg = !res.success ? res.message : 'Could not load case update data.';
        setDraft(null);
        setInitial(null);
        setStep1(undefined);
        setStep3(undefined);
        setStep4(undefined);
        setLoadError(errMsg ?? 'Could not load case update data.');
        setLoadedForKey(loadKey);
        return;
      }

      const applied = applyViewData(res.data, recentSubmittedAt);
      setDraft(applied.draft);
      setInitial(cloneDraft(applied.draft));
      setStep1(applied.step1);
      setStep3(applied.step3);
      setStep4(applied.step4);
      setSubmittedLabel(applied.submittedLabel);
      setLoadError('');
      setLoadedForKey(loadKey);
    })();

    return () => {
      cancelled = true;
    };
  }, [caseRequestId, loadKey, recentSubmittedAt, useSharedViewData, viewData, viewDataLoading]);

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));

  const showHighCourt = useMemo(
    () => isBriberySubCategory(draft?.subFileCategoryName || ''),
    [draft?.subFileCategoryName],
  );

  const caseSourceIsOther = useMemo(() => {
    if (!draft) return false;
    const item = lookups.caseSources.find((s) => s.id === draft.caseSourceId);
    return isOtherLookup(draft.caseSource, item?.code ?? draft.caseSourceId);
  }, [draft, lookups.caseSources]);

  const maxDob = useMemo(() => todayIsoDateLocal(), []);
  const maxOffenceDate = useMemo(() => todayIsoDateLocal(), []);
  const minDeadlineDate = useMemo(() => tomorrowIsoDateLocal(), []);
  const victimDob = draft?.victimDob ?? '';
  const age = useMemo(() => (victimDob ? calcAgeFromDob(victimDob) : null), [victimDob]);
  const under18 = typeof age === 'number' && age < 18;

  const urgencyLabel = useMemo(() => {
    if (!draft) return '';
    return (
      step3?.urgency_level_name?.trim() ||
      lookups.urgencyLevels.find((u) => u.id === draft.urgencyLevelId)?.name ||
      ''
    );
  }, [draft, lookups.urgencyLevels, step3?.urgency_level_name]);

  const submitter = useMemo(
    () => (draft ? submitterFromStep4(step4, draft) : null),
    [draft, step4],
  );

  const canSaveStep1 = useMemo(() => canSaveCaseUpdateStep(1, draft, initial), [draft, initial]);
  const canSaveStep2 = useMemo(() => canSaveCaseUpdateStep(2, draft, initial), [draft, initial]);
  const canSaveStep3 = useMemo(() => canSaveCaseUpdateStep(3, draft, initial), [draft, initial]);

  const validateStep1 = (): boolean => {
    if (!draft) return false;
    const specs: RequiredFieldSpec[] = [
      { key: 'caseSourceId', label: 'Case Source', value: draft.caseSourceId, elementId: 'cu-caseSource' },
      { key: 'provinceId', label: 'Province', value: draft.provinceId, elementId: 'cu-province' },
      { key: 'districtId', label: 'District', value: draft.districtId, elementId: 'cu-district' },
      { key: 'magistrateCourtId', label: 'Magistrate Court Name', value: draft.magistrateCourtId, elementId: 'cu-magistrateCourt' },
      { key: 'policeStationId', label: 'Police Station', value: draft.policeStationId, elementId: 'cu-policeStation' },
      {
        key: 'yourReferenceNumber',
        label: 'Your Reference Number (External)',
        value: draft.yourReferenceNumber,
        elementId: 'cu-yourRef',
      },
      { key: 'hasConnectedMatters', label: 'Connected matters', value: draft.hasConnectedMatters, elementId: 'cu-connected' },
    ];
    if (draft.hasConnectedMatters === 'yes') {
      specs.push(
        { key: 'connectedCaseNumber', label: 'Connected Case Number', value: draft.connectedCaseNumber, elementId: 'cu-connectedCase' },
      );
      if (draft.connectedAgdFileRefAvailable) {
        specs.push({
          key: 'connectedAgdFileRef',
          label: 'Connected AGD File Reference Number',
          value: draft.connectedAgdFileRef,
          elementId: 'cu-connectedAgd',
        });
      }
    }
    if (caseSourceIsOther) {
      specs.push({
        key: 'caseSourceOther',
        label: 'Case Source (Other)',
        value: draft.caseSourceOther,
        elementId: 'cu-caseSourceOther',
      });
    }

    const reqErrs = validateRequired(specs);
    const extra: RequiredErrors = {};
    if (exceedsMax(draft.description, MAX_DESC)) extra.description = maxCharLimitExceededError();
    if (exceedsMax(draft.yourReferenceNumber, MAX_REF)) extra.yourReferenceNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.otherExternalReferences, MAX_REF)) extra.otherExternalReferences = maxCharLimitExceededError();
    if (exceedsMax(draft.caseNumber, MAX_REF)) extra.caseNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.connectedCaseNumber, MAX_REF)) extra.connectedCaseNumber = maxCharLimitExceededError();
    if (exceedsMax(draft.connectedAgdFileRef, MAX_REF)) extra.connectedAgdFileRef = maxCharLimitExceededError();

    const all = { ...reqErrs, ...extra };
    setErrors(all);
    if (Object.keys(all).length) {
      focusFirstInvalid(specs, all);
      return false;
    }
    return true;
  };

  const validateStep2 = (): boolean => {
    if (!draft) return false;
    const extra: RequiredErrors = {};
    if (exceedsMax(draft.victimName, MAX_REF)) extra.victimName = maxCharLimitExceededError();
    setErrors(extra);
    if (Object.keys(extra).length) {
      focusFirstInvalid(
        [{ key: 'victimName', label: 'Name of the Victim', value: draft.victimName, elementId: 'cu-victimName' }],
        extra,
      );
      return false;
    }
    return true;
  };

  const validateStep3 = (): boolean => {
    if (!draft) return false;
    const specs: RequiredFieldSpec[] = [
      { key: 'offenceDateFrom', label: 'Date of Offence', value: draft.offenceDateFrom, elementId: 'cu-offenceFrom' },
      { key: 'urgencyLevelId', label: 'Urgency Level', value: draft.urgencyLevelId, elementId: 'cu-urgency' },
    ];

    if (draft.accusedDetailsAvailable) {
      draft.accused.forEach((a, i) => {
        specs.push(
          { key: `accused-name-${i}`, label: 'Accused / Suspect Name', value: a.name, elementId: `cu-accused-name-${i}` },
          { key: `accused-status-${i}`, label: 'Accused / Suspect Status', value: a.status, elementId: `cu-accused-status-${i}` },
        );
      });
    }

    const reqErrs = validateRequired(specs);
    const extra: RequiredErrors = {};

    if (draft.offenceDateFrom && isFutureIsoDate(draft.offenceDateFrom)) {
      extra.offenceDateFrom = 'Date of Offence cannot be in the future.';
    }

    if (draft.deadline && !isFutureIsoDate(draft.deadline)) {
      extra.deadline = 'Deadline must be a future date.';
    }

    if (exceedsMax(draft.placeOfOffence, MAX_TEXT)) extra.placeOfOffence = maxCharLimitExceededError();
    if (exceedsMax(draft.urgencyJustification, MAX_TEXT)) extra.urgencyJustification = maxCharLimitExceededError();

    if (draft.accusedDetailsAvailable) {
      draft.accused.forEach((a, i) => {
        if (a.name.trim() && exceedsMax(a.name, MAX_TEXT)) extra[`accused-name-${i}`] = maxCharLimitExceededError();
        if (isStatusOther(a.status, lookups.suspectStatuses) && !a.statusOther.trim()) {
          extra[`accused-statusOther-${i}`] = 'Status (Other) is required.';
        }
      });
    }

    if (draft.urgencyLevelId && !draft.urgencyJustification.trim()) {
      extra.urgencyJustification = 'Urgency Justification is required.';
    }

    const all = { ...reqErrs, ...extra };
    setErrors(all);
    if (Object.keys(all).length) {
      const allSpecs: RequiredFieldSpec[] = [
        ...specs,
        {
          key: 'urgencyJustification',
          label: 'Urgency Justification',
          value: draft.urgencyJustification,
          elementId: 'cu-urgencyJustification',
        },
        { key: 'deadline', label: 'Deadline (if any)', value: draft.deadline, elementId: 'cu-deadline' },
        ...(draft.accusedDetailsAvailable
          ? draft.accused.flatMap((a, i) =>
              isStatusOther(a.status, lookups.suspectStatuses)
                ? [{ key: `accused-statusOther-${i}`, label: 'Status (Other)', value: a.statusOther, elementId: `cu-accused-statusOther-${i}` }]
                : [],
            )
          : []),
      ];
      focusFirstInvalid(allSpecs, all);
      return false;
    }
    return true;
  };

  const saveSection = async (step: CriminalWizardStep) => {
    if (!draft || readOnly || saveInFlightRef.current) return;
    const validators: Record<CriminalWizardStep, () => boolean> = {
      1: validateStep1,
      2: validateStep2,
      3: validateStep3,
      4: () => true,
      5: () => true,
    };
    if (!validators[step]()) return;

    saveInFlightRef.current = true;
    setSavingStep(step);
    try {
      const r = await saveCriminalRequest(draft, {
        isDraft: false,
        confirmedCorrect: true,
        currentStep: step,
        isUpdateAlreadySubmitted: true,
      });

      if (!r.success) {
        setFlashToast({ message: r.message ?? 'Failed to save case update.', variant: 'error' });
        return;
      }

      const next = {
        ...draft,
        draftRequestId: r.data.requestId || draft.draftRequestId,
        referenceNo: r.data.fileReferenceNumber ?? draft.referenceNo,
      };
      setDraft(next);
      setInitial((prev) => {
        if (!prev) return cloneDraft(next);
        if (step === 1 || step === 2 || step === 3) {
          return mergeCriminalSavedStepIntoInitial(step, prev, next, cloneDraft);
        }
        return cloneDraft(next);
      });
      setErrors({});
      setFlashToast({ message: 'Case update saved successfully.', variant: 'success' });
    } finally {
      saveInFlightRef.current = false;
      setSavingStep(null);
    }
  };

  const resetSection = () => {
    if (initial) {
      setDraft(cloneDraft(initial));
      setErrors({});
    }
  };

  const beginCancel = () => {
    if (isSaving || !draft || !initial) return;
    if (draftHasUnsavedChanges(draft, initial)) {
      setCancelConfirmOpen(true);
      return;
    }
    resetSection();
  };

  const confirmCancel = () => {
    confirmCaseUpdateCancel(resetSection, () => setCancelConfirmOpen(false), onCancelConfirmed);
  };

  const updateAccused = (id: string, patch: Partial<AccusedRecord>) => {
    setDraft((d) => (d ? { ...d, accused: d.accused.map((a) => (a.id === id ? { ...a, ...patch } : a)) } : d));
  };

  if (loading) {
    return <p className="tcd-update-ts">Loading case update…</p>;
  }

  if (!draft) {
    return (
      <div className="tcd-case-update-error" role="alert">
        <p className="tcd-update-ts" style={{ color: 'var(--red)', margin: 0 }}>
          {loadError || 'Could not load case update data.'}
        </p>
      </div>
    );
  }

  const fileCategoryLabel = draft.fileCategory || step1?.category_name || '—';
  const subCategoryLabel = draft.subFileCategoryName || step1?.sub_category_name || '—';

  return (
    <>
      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title="Cancel Changes"
        message="Are you sure you want to cancel? Any unsaved changes will be lost."
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={confirmCancel}
      />

      <div className="tcd-case-update" aria-readonly={readOnly || undefined}>
      <fieldset
        className="tcd-case-update-fieldset"
        disabled={readOnly}
        style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}
      >
      <p className="tcd-update-ts">
        Request Submitted at: <strong>{submittedLabel}</strong>
      </p>

      <ReviewSection step="Step 01" title="Case Overview">
        <ReviewSubBlock title="Case Intake Details">
          <ReviewGrid cols={3}>
            <ReviewField label="File Category" value={fileCategoryLabel} />
            <ReviewField label="Sub File Category" value={subCategoryLabel} />
          </ReviewGrid>
          <ReviewChips items={draft.applicableAreas} />
          <div className="ccr-fg" style={{ marginBottom: 14 }}>
            <label>Description</label>
            <textarea
              id="cu-description"
              className={inpClass(Boolean(errors.description))}
              rows={4}
              maxLength={MAX_DESC}
              value={draft.description}
              readOnly={readOnly}
              disabled={readOnly}
              onChange={(e) => {
                setDraft((d) => (d ? { ...d, description: sliceMax(e.target.value, MAX_DESC) } : d));
                clearErr('description');
              }}
              placeholder="Enter a detailed description of the request"
            />
            <FieldError message={errors.description} />
          </div>

          <div className="ccr-fr c2">
            <div className="ccr-fg">
              <label>
                Case Source <span className="req">*</span>
              </label>
              <WizardSelect
                id="cu-caseSource"
                hasError={Boolean(errors.caseSourceId)}
                value={draft.caseSourceId}
                disabled={readOnly}
                placeholder="Select Case Source"
                options={toSelectOpts(lookups.caseSources)}
                onChange={(id) => {
                  const name = lookups.caseSources.find((s) => s.id === id)?.name ?? '';
                  setDraft((d) =>
                    d
                      ? {
                          ...d,
                          caseSourceId: id,
                          caseSource: name,
                          caseSourceOther: isOtherLookup(name, id) ? d.caseSourceOther : '',
                        }
                      : d,
                  );
                  clearErr('caseSourceId');
                }}
              />
              <FieldError message={errors.caseSourceId} />
            </div>

            {caseSourceIsOther ? (
              <div className="ccr-fg">
                <label>
                  Case Source (Other) <span className="req">*</span>
                </label>
                <input
                  id="cu-caseSourceOther"
                  className={inpClass(Boolean(errors.caseSourceOther))}
                  type="text"
                  maxLength={MAX_REF}
                  value={draft.caseSourceOther}
                  readOnly={readOnly}
                  disabled={readOnly}
                  onChange={(e) => {
                    setDraft((d) => (d ? { ...d, caseSourceOther: sliceMax(e.target.value, MAX_REF) } : d));
                    clearErr('caseSourceOther');
                  }}
                />
                <FieldError message={errors.caseSourceOther} />
              </div>
            ) : (
              <div />
            )}

            <div className="ccr-fg">
              <label>
                Province <span className="req">*</span>
              </label>
              <WizardSelect
                id="cu-province"
                hasError={Boolean(errors.provinceId)}
                value={draft.provinceId}
                disabled={readOnly}
                placeholder="Select Province"
                options={toSelectOpts(lookups.provinces)}
                onChange={(id) => {
                  setDraft((d) =>
                    d
                      ? {
                          ...d,
                          provinceId: id,
                          districtId: '',
                          magistrateCourtId: '',
                          highCourtId: '',
                          policeStationId: '',
                        }
                      : d,
                  );
                  clearErr('provinceId');
                }}
              />
              <FieldError message={errors.provinceId} />
            </div>

            <div className="ccr-fg">
              <label>
                District <span className="req">*</span>
              </label>
              <WizardSelect
                id="cu-district"
                hasError={Boolean(errors.districtId)}
                value={draft.districtId}
                disabled={readOnly || !draft.provinceId}
                placeholder={draft.provinceId ? 'Select District' : 'Select province first'}
                options={toSelectOpts(lookups.districts)}
                onChange={(id) => {
                  setDraft((d) =>
                    d ? { ...d, districtId: id, magistrateCourtId: '', highCourtId: '', policeStationId: '' } : d,
                  );
                  clearErr('districtId');
                }}
              />
              <FieldError message={errors.districtId} />
            </div>

            {showHighCourt ? (
              <div className="ccr-fg">
                <label>High Court Name</label>
                <WizardSelect
                  value={draft.highCourtId}
                  disabled={readOnly || !draft.districtId}
                  placeholder={draft.districtId ? 'Select High Court' : 'Select district first'}
                  options={toSelectOpts(lookups.highCourts)}
                  onChange={(id) => setDraft((d) => (d ? { ...d, highCourtId: id } : d))}
                />
              </div>
            ) : null}

            <div className="ccr-fg">
              <label>
                Magistrate Court Name <span className="req">*</span>
              </label>
              <WizardSelect
                id="cu-magistrateCourt"
                hasError={Boolean(errors.magistrateCourtId)}
                value={draft.magistrateCourtId}
                disabled={readOnly || !draft.districtId}
                placeholder={draft.districtId ? 'Select Magistrate Court' : 'Select district first'}
                options={toSelectOpts(lookups.magistrateCourts)}
                onChange={(id) => {
                  setDraft((d) => (d ? { ...d, magistrateCourtId: id } : d));
                  clearErr('magistrateCourtId');
                }}
              />
              <FieldError message={errors.magistrateCourtId} />
            </div>

            <div className="ccr-fg">
              <label>
                Police Station <span className="req">*</span>
              </label>
              <WizardSelect
                id="cu-policeStation"
                hasError={Boolean(errors.policeStationId)}
                value={draft.policeStationId}
                disabled={readOnly || !draft.districtId}
                placeholder={draft.districtId ? 'Select Police Station' : 'Select district first'}
                options={toSelectOpts(lookups.policeStations)}
                onChange={(id) => {
                  setDraft((d) => (d ? { ...d, policeStationId: id } : d));
                  clearErr('policeStationId');
                }}
              />
              <FieldError message={errors.policeStationId} />
            </div>

            <div className="ccr-fg">
              <label>
                Your Reference Number (External) <span className="req">*</span>
              </label>
              <input
                id="cu-yourRef"
                className={inpClass(Boolean(errors.yourReferenceNumber))}
                value={draft.yourReferenceNumber}
                readOnly={readOnly}
                disabled={readOnly}
                maxLength={MAX_REF}
                onChange={(e) => {
                  setDraft((d) => (d ? { ...d, yourReferenceNumber: sliceMax(e.target.value, MAX_REF) } : d));
                  clearErr('yourReferenceNumber');
                }}
              />
              <FieldError message={errors.yourReferenceNumber} />
            </div>

            <div className="ccr-fg">
              <label>Other External Reference Number(s)</label>
              <input
                className={inpClass(Boolean(errors.otherExternalReferences))}
                value={draft.otherExternalReferences}
                readOnly={readOnly}
                disabled={readOnly}
                maxLength={MAX_REF}
                onChange={(e) => {
                  setDraft((d) => (d ? { ...d, otherExternalReferences: sliceMax(e.target.value, MAX_REF) } : d));
                  clearErr('otherExternalReferences');
                }}
              />
              <FieldError message={errors.otherExternalReferences} />
            </div>

            <div className="ccr-fg">
              <label>Case Number</label>
              <input
                className={inpClass(Boolean(errors.caseNumber))}
                value={draft.caseNumber}
                readOnly={readOnly}
                disabled={readOnly}
                maxLength={MAX_REF}
                onChange={(e) => {
                  setDraft((d) => (d ? { ...d, caseNumber: sliceMax(e.target.value, MAX_REF) } : d));
                  clearErr('caseNumber');
                }}
              />
              <FieldError message={errors.caseNumber} />
            </div>

            <div className="ccr-fg">
              <label>
                Are there any connected matters (Cases)? <span className="req">*</span>
              </label>
              <div id="cu-connected" className="ccr-radio-row">
                {(['yes', 'no'] as const).map((v) => (
                  <label key={v} className="ccr-radio">
                    <input
                      type="radio"
                      name="cu-connected"
                      checked={draft.hasConnectedMatters === v}
                      disabled={readOnly}
                      onChange={() => {
                        setDraft((d) =>
                          d
                            ? {
                                ...d,
                                hasConnectedMatters: v,
                                connectedCaseNumber: v === 'yes' ? d.connectedCaseNumber : '',
                                connectedAgdFileRefAvailable: v === 'yes' ? d.connectedAgdFileRefAvailable : false,
                                connectedAgdFileRef: v === 'yes' ? d.connectedAgdFileRef : '',
                              }
                            : d,
                        );
                        clearErr('hasConnectedMatters');
                      }}
                    />
                    {v === 'yes' ? 'Yes' : 'No'}
                  </label>
                ))}
              </div>
              <FieldError message={errors.hasConnectedMatters} />
            </div>

            {draft.hasConnectedMatters === 'yes' ? (
              <>
                <div className="ccr-fg">
                  <label>
                    Connected Case Number <span className="req">*</span>
                  </label>
                  <input
                    id="cu-connectedCase"
                    className={inpClass(Boolean(errors.connectedCaseNumber))}
                    value={draft.connectedCaseNumber}
                    readOnly={readOnly}
                    disabled={readOnly}
                    maxLength={MAX_REF}
                    onChange={(e) => {
                      setDraft((d) => (d ? { ...d, connectedCaseNumber: sliceMax(e.target.value, MAX_REF) } : d));
                      clearErr('connectedCaseNumber');
                    }}
                  />
                  <FieldError message={errors.connectedCaseNumber} />
                </div>
                <div className="ccr-fg">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <label style={{ marginBottom: 0 }}>
                      Connected AGD File Reference Number{' '}
                      {draft.connectedAgdFileRefAvailable ? <span className="req">*</span> : null}
                    </label>
                    <input
                      type="checkbox"
                      style={{ width: 15, height: 15, accentColor: '#000', cursor: 'pointer' }}
                      checked={draft.connectedAgdFileRefAvailable}
                      disabled={readOnly}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setDraft((d) =>
                          d
                            ? {
                                ...d,
                                connectedAgdFileRefAvailable: checked,
                                connectedAgdFileRef: checked ? d.connectedAgdFileRef : '',
                              }
                            : d,
                        );
                        clearErr('connectedAgdFileRef');
                      }}
                    />
                    <span>Available</span>
                  </div>
                  <input
                    id="cu-connectedAgd"
                    className={inpClass(Boolean(errors.connectedAgdFileRef))}
                    value={draft.connectedAgdFileRef}
                    readOnly={readOnly || !draft.connectedAgdFileRefAvailable}
                    disabled={readOnly || !draft.connectedAgdFileRefAvailable}
                    maxLength={MAX_REF}
                    onChange={(e) => {
                      setDraft((d) => (d ? { ...d, connectedAgdFileRef: sliceMax(e.target.value, MAX_REF) } : d));
                      clearErr('connectedAgdFileRef');
                    }}
                  />
                  <div className="ccr-slbl">
                    If you are unaware of the relevant Connected AGD File Reference Number details, please leave the
                    &apos;Available&apos; checkbox unchecked.
                  </div>
                  <FieldError message={errors.connectedAgdFileRef} />
                </div>
              </>
            ) : null}
          </div>
        </ReviewSubBlock>
        {!readOnly ? (
          <div className="tcd-step-actions">
            <button type="button" className="ccr-btn-ol" onClick={beginCancel} disabled={isSaving}>
              Cancel
            </button>
            <button type="button" className="ccr-btn-pr" onClick={() => void saveSection(1)} disabled={isSaving || !canSaveStep1}>
              {savingStep === 1 ? 'Saving…' : 'Save'}
            </button>
          </div>
        ) : null}
      </ReviewSection>

      <ReviewSection step="Step 02" title="Victim Details" defaultOpen={false}>
        <ReviewSubBlock title="Victim Details">
          <div className="ccr-fr c2">
            <div className="ccr-fg">
              <label>Name of the Victim</label>
              <input
                id="cu-victimName"
                className={inpClass(Boolean(errors.victimName))}
                type="text"
                maxLength={MAX_REF}
                value={draft.victimName}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) => (d ? { ...d, victimName: sliceMax(e.target.value, MAX_REF) } : d));
                  clearErr('victimName');
                }}
                placeholder="Enter Name of the Victim"
              />
              <FieldError message={errors.victimName} />
            </div>
            <div className="ccr-fg">
              <label>Date of Birth</label>
              <FormattedDateInput
                value={draft.victimDob}
                maxDate={maxDob}
                enableYmdViews
                disabled={readOnly}
                onChange={(next) => setDraft((d) => (d ? { ...d, victimDob: next } : d))}
              />
              {under18 ? (
                <div className="under18">
                  <IconWarningTriangle />
                  Child is under 16 years
                </div>
              ) : null}
            </div>
            <div className="ccr-fg">
              <label>Age</label>
              <input
                className="ccr-inp"
                type="text"
                value={typeof age === 'number' ? String(age) : ''}
                readOnly
                disabled
                placeholder=""
              />
            </div>
            <div className="ccr-fg">
              <label>Gender</label>
              <WizardSelect
                value={draft.victimGenderId}
                disabled={readOnly}
                placeholder="Select Gender"
                options={toSelectOpts(lookups.genders)}
                onChange={(id) => {
                  const name = lookups.genders.find((g) => g.id === id)?.name ?? '';
                  setDraft((d) => (d ? { ...d, victimGenderId: id, victimGender: name } : d));
                }}
              />
            </div>
          </div>
        </ReviewSubBlock>
        {!readOnly ? (
          <div className="tcd-step-actions">
            <button type="button" className="ccr-btn-ol" onClick={beginCancel} disabled={isSaving}>
              Cancel
            </button>
            <button type="button" className="ccr-btn-pr" onClick={() => void saveSection(2)} disabled={isSaving || !canSaveStep2}>
              {savingStep === 2 ? 'Saving…' : 'Save'}
            </button>
          </div>
        ) : null}
      </ReviewSection>

      <ReviewSection step="Step 03" title="Offence & Accused Details" defaultOpen={false}>
        <ReviewSubBlock title="Offence Details">
          <div className="ccr-fr c2">
            <div className="ccr-fg">
              <label>
                Date of Offence <span className="req">*</span>
              </label>
              <FormattedDateInput
                id="cu-offenceFrom"
                value={draft.offenceDateFrom}
                maxDate={maxOffenceDate}
                enableYmdViews
                disabled={readOnly}
                onChange={(next) => {
                  setDraft((d) => (d ? { ...d, offenceDateFrom: next, offenceDateTo: next } : d));
                  clearErr('offenceDateFrom');
                }}
              />
              <FieldError message={errors.offenceDateFrom} />
            </div>
            <div className="ccr-fg">
              <label>Place of Offence</label>
              <input
                id="cu-placeOfOffence"
                className={inpClass(Boolean(errors.placeOfOffence))}
                type="text"
                maxLength={MAX_TEXT}
                value={draft.placeOfOffence}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) => (d ? { ...d, placeOfOffence: sliceMax(e.target.value, MAX_TEXT) } : d));
                  clearErr('placeOfOffence');
                }}
                placeholder="Enter place of offence"
              />
              <FieldError message={errors.placeOfOffence} />
            </div>
          </div>

          <div className="ccr-fg" style={{ marginTop: 14 }}>
            <label className="ccr-check">
              <input
                id="cu-accusedDetailsAvailable"
                type="checkbox"
                checked={draft.accusedDetailsAvailable}
                disabled={readOnly}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setDraft((d) => (d ? { ...d, accusedDetailsAvailable: checked } : d));
                  if (!checked) {
                    setErrors((prev) => {
                      const next = { ...prev };
                      for (const key of Object.keys(next)) {
                        if (key.startsWith('accused-')) delete next[key];
                      }
                      return next;
                    });
                  }
                }}
              />
              <span>
                Accused/Suspect Details Available <span className="req">*</span>
              </span>
            </label>
          </div>
        </ReviewSubBlock>

        {draft.accusedDetailsAvailable ? (
        <div className="ccr-accused-panel">
          <div className="ccr-accused-panel-hdr">Accused / Suspects</div>
          <div className="ccr-accused-panel-body">
            {draft.accused.map((row, idx) => (
              <div key={row.id} style={{ marginBottom: idx < draft.accused.length - 1 ? 12 : 0 }}>
                <div className="ccr-accused-row">
                  <div className="ccr-fg">
                    <label>Accused / Suspect Name</label>
                    <input
                      id={`cu-accused-name-${idx}`}
                      className={inpClass(Boolean(errors[`accused-name-${idx}`]))}
                      type="text"
                      maxLength={MAX_TEXT}
                      value={row.name}
                      readOnly={readOnly}
                      disabled={readOnly}
                      onChange={(e) => updateAccused(row.id, { name: sliceMax(e.target.value, MAX_TEXT) })}
                      placeholder="Enter Accused/Suspect Name"
                    />
                    <FieldError message={errors[`accused-name-${idx}`]} />
                  </div>
                  <div className="ccr-fg">
                    <label>Accused / Suspect Status</label>
                    <WizardSelect
                      id={`cu-accused-status-${idx}`}
                      hasError={Boolean(errors[`accused-status-${idx}`])}
                      value={row.status}
                      disabled={readOnly}
                      placeholder="Select Status"
                      options={toSelectOpts(lookups.suspectStatuses)}
                      onChange={(id) => updateAccused(row.id, { status: id, statusOther: '' })}
                    />
                    <FieldError message={errors[`accused-status-${idx}`]} />
                  </div>
                  {!readOnly ? (
                    <button
                      type="button"
                      className="ccr-accused-del"
                      aria-label={`Remove accused ${idx + 1}`}
                      disabled={draft.accused.length <= 1}
                      onClick={() =>
                        setDraft((d) =>
                          d && d.accused.length > 1
                            ? { ...d, accused: d.accused.filter((a) => a.id !== row.id) }
                            : d,
                        )
                      }
                    >
                      <TrashIcon />
                    </button>
                  ) : (
                    <span />
                  )}
                </div>
                {isStatusOther(row.status, lookups.suspectStatuses) ? (
                  <div className="ccr-accused-other ccr-fg">
                    <label>
                      Status (Other) <span className="req">*</span>
                    </label>
                    <input
                      className={inpClass(Boolean(errors[`accused-statusOther-${idx}`]))}
                      type="text"
                      maxLength={255}
                      value={row.statusOther}
                      readOnly={readOnly}
                      disabled={readOnly}
                      onChange={(e) => updateAccused(row.id, { statusOther: sliceMax(e.target.value, 255) })}
                      placeholder="Specify status"
                    />
                    <FieldError message={errors[`accused-statusOther-${idx}`]} />
                  </div>
                ) : null}
              </div>
            ))}
            {!readOnly ? (
              <button
                type="button"
                className="ccr-btn-ol"
                style={{ marginTop: 14 }}
                onClick={() => setDraft((d) => (d ? { ...d, accused: [...d.accused, newAccused()] } : d))}
              >
                + Add Accused/Suspect
              </button>
            ) : null}
          </div>
        </div>
        ) : null}

        <ReviewSubBlock title="Timeline">
          <div className="ccr-fr c2">
            <div className="ccr-fg">
              <label>
                Urgency Level <span className="req">*</span>
              </label>
              <WizardSelect
                id="cu-urgency"
                hasError={Boolean(errors.urgencyLevelId)}
                value={draft.urgencyLevelId}
                disabled={readOnly}
                placeholder="Select Urgency Level"
                options={toSelectOpts(lookups.urgencyLevels)}
                onChange={(id) => {
                  setDraft((d) => (d ? { ...d, urgencyLevelId: id } : d));
                  clearErr('urgencyLevelId');
                }}
              />
              <FieldError message={errors.urgencyLevelId} />
            </div>
            <div className="ccr-fg">
              <label>Deadline (if any)</label>
              <FormattedDateInput
                id="cu-deadline"
                value={draft.deadline}
                minDate={minDeadlineDate}
                disabled={readOnly}
                onChange={(next) => {
                  setDraft((d) => (d ? { ...d, deadline: next } : d));
                  clearErr('deadline');
                }}
              />
              <FieldError message={errors.deadline} />
            </div>
            <div className="ccr-fg" style={{ gridColumn: '1 / -1' }}>
              <label>
                Urgency Justification <span className="req">*</span>
              </label>
              <textarea
                id="cu-urgencyJustification"
                className={inpClass(Boolean(errors.urgencyJustification))}
                rows={3}
                maxLength={MAX_TEXT}
                value={draft.urgencyJustification}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) => (d ? { ...d, urgencyJustification: sliceMax(e.target.value, MAX_TEXT) } : d));
                  clearErr('urgencyJustification');
                }}
                placeholder="Enter urgency justification"
              />
              <FieldError message={errors.urgencyJustification} />
            </div>
          </div>
          {urgencyLabel && readOnly ? (
            <p className="tcd-update-ts" style={{ marginTop: 8 }}>
              Selected urgency: <strong>{urgencyLabel}</strong>
            </p>
          ) : null}
        </ReviewSubBlock>
        {!readOnly ? (
          <div className="tcd-step-actions">
            <button type="button" className="ccr-btn-ol" onClick={beginCancel} disabled={isSaving}>
              Cancel
            </button>
            <button type="button" className="ccr-btn-pr" onClick={() => void saveSection(3)} disabled={isSaving || !canSaveStep3}>
              {savingStep === 3 ? 'Saving…' : 'Save'}
            </button>
          </div>
        ) : null}
      </ReviewSection>

      <ReviewSection step="Step 04" title="Submission & Physical File Delivery" defaultOpen={false}>
        <ReviewSubBlock title="Request Submitter's Details">
          <ReviewGrid cols={3}>
            <ReviewField label="Submitted Agency Name" value={submitter?.agency ?? '—'} />
            <ReviewField label="Designation" value={submitter?.designation ?? '—'} />
            <ReviewField label="Submitted by" value={submitter?.name ?? '—'} />
            <ReviewField label="Email Address" value={submitter?.email ?? '—'} />
            <ReviewField label="Contact Number" value={submitter?.contact ?? '—'} />
          </ReviewGrid>
        </ReviewSubBlock>
        <ReviewSubBlock title="Physical File Delivery Method">
          <ReviewGrid cols={3}>
            <ReviewField label="Physical File Delivery Method" value={submitter?.deliveryMethod ?? '—'} span={3} />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>
      </fieldset>
    </div>
    </>
  );
}
